// src/lib/api/stream.ts — streaming / recording / signalling (PRI-291 Phase 7 → PRI-439 R15)
// uploadStreamRecording, presignStreamRecording, getStreamRecordingUrl,
// TURN credentials, stream config, signalling URL — ALL stream endpoint
// literals live here now (§25.1: streaming UI → public streams plane).

import { API_BASE_URL } from './client'
// PRI-496 P0: the singular /v1/stream/* plane is INTERNAL (public contract is
// /v1/streams). All calls route through the internal-api-client chokepoint —
// pure delegation, zero behavior change.
import { internalRequest as request, internalFetch, internalApiUrl } from '../internal-api-client'

/** WebSocket signalling URL for live streams (public streams plane, §4.8). */
export function getStreamSignalUrl(apiBase: string = API_BASE_URL): string {
  return `${apiBase.replace('https://', 'wss://').replace('http://', 'ws://')}/v1/stream/signal`
}

export interface TurnResult {
  servers: RTCIceServer[] | undefined
  failed: boolean
}

/** Single fetch attempt — used internally by getTurnCredentials. */
async function _tryFetchTurn(
  jwtToken: string | null,
  opts: { apiBase?: string; timeoutMs?: number },
): Promise<TurnResult> {
  try {
    const res = await internalFetch('/v1/stream/turn-credentials', {
      base: opts.apiBase,
      headers: jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {},
      signal: AbortSignal.timeout(opts.timeoutMs ?? 5000),
    })
    if (!res.ok) return { servers: undefined, failed: true }
    const d = await res.json() as { ice_servers?: RTCIceServer[] }
    const servers = d.ice_servers?.length ? d.ice_servers : undefined
    return servers ? { servers, failed: false } : { servers: undefined, failed: true }
  } catch {
    return { servers: undefined, failed: true }
  }
}

/** Fetch TURN/ICE servers. Requires auth (PRI-433 P4).
 * Returns TurnResult. Retries once after 2s on failure.
 * Callers: check result.failed to emit STUN-fallback telemetry. */
export async function getTurnCredentials(
  jwtToken: string | null,
  opts: { apiBase?: string; timeoutMs?: number } = {},
): Promise<TurnResult> {
  const first = await _tryFetchTurn(jwtToken, opts)
  if (!first.failed) return first
  // Single retry after 2s
  await new Promise<void>(r => setTimeout(r, 2_000))
  return _tryFetchTurn(jwtToken, opts)
}

/** Public read-only stream config (PRI-388). Returns {} on failure. */
export async function getStreamConfig(
  opts: { apiBase?: string; timeoutMs?: number } = {},
): Promise<Record<string, unknown>> {
  try {
    const res = await internalFetch('/v1/stream/config', {
      base: opts.apiBase,
      signal: AbortSignal.timeout(opts.timeoutMs ?? 3000),
    })
    if (res.ok) return await res.json() as Record<string, unknown>
  } catch { /* fall through */ }
  return {}
}

/** Get a signed playback URL for a stream recording via GET /v1/stream/recording.
 * Returns the API endpoint URL (which redirects to a signed CloudFront URL). */
export function getStreamRecordingUrl(s3Key: string): string {
  return internalApiUrl(`/v1/stream/recording?key=${encodeURIComponent(s3Key)}`);
}

/** Presign an upload for a stream recording — uploads to RESULTS bucket (CloudFront-served) */
export async function presignStreamRecording(data: {
  filename: string;
  file_size_bytes: number;
  content_type: string;
}): Promise<{ upload_url: string; s3_key: string; expires_in: number }> {
  return request('/v1/stream/presign-recording', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Upload a stream recording through the API. Avoids browser-direct S3 PUT/CORS. */
export async function uploadStreamRecording(data: {
  filename: string;
  blob: Blob;
}): Promise<{ s3_key: string }> {
  const form = new FormData();
  form.append('file', data.blob, data.filename);
  return request('/v1/stream/recording-upload', {
    method: 'POST',
    body: form,
    skipContentType: true,
  });
}
